import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingBlock,
  Mono,
  Table,
  Td,
  Th,
  cx,
} from "../../components/ui";
import { Icon } from "../../components/icons";
import { UsageChart } from "../../components/UsageChart";
import { relativeTime } from "../../lib/format";
import {
  EMPTY_TOTALS,
  USAGE_SERIES,
  formatExactTokens,
  formatRequestTokens,
  keySpendLabel,
  usageSeries,
  type UsageSeriesKey,
} from "../../lib/inference";
import { formatUsageCost } from "../../lib/money";
import { useInferenceUsage } from "../../lib/queries";
import type { InferenceKeySpend, InferenceModelSpend } from "../../lib/types";
import { useAiTabContext } from "./context";
import { Panel, Segmented, ShareRule } from "./parts";
import { StatStrip } from "./StatStrip";

/**
 * What inference has cost, and which of your keys spent it.
 *
 * The second half of that sentence is new, and it is the reason this is its own
 * tab. `inference_usage.api_key_id` has been written on every row since inference
 * existed and was never readable, so "which of our services is burning this" —
 * the first question asked when a bill moves — had no answer anywhere in the
 * product. A per-key breakdown is the answer, and it only makes sense beside a
 * range and a filter, which is more than fitted on a shared page.
 *
 * Three deliberate asymmetries:
 *
 *   * The range moves the chart, both breakdowns and the strip. It does not move
 *     the month-to-date figure, which lives on Overview and ties to the billing
 *     page — a range picker that silently redefined "this month" would put two
 *     screens in disagreement about what is owed.
 *   * Filtering narrows the request log only, never the breakdowns. The
 *     breakdowns are how somebody chooses what to filter by, so recomputing them
 *     under the filter would collapse the list to the one row already picked and
 *     leave no way back.
 *   * The export is the whole range, ignoring the filter. It exists for the
 *     questions this page cannot answer, and pre-narrowing it would answer one of
 *     them for the reader instead of handing over the rows.
 */
export function ActivityTab() {
  const { t } = useTranslation("ai");
  const { orgId } = useAiTabContext();

  const [days, setDays] = useState(30);
  const [series, setSeries] = useState<UsageSeriesKey>("spend");
  const [filter, setFilter] = useState<{ model?: string; keyId?: string }>({});

  const usage = useInferenceUsage(orgId, { days, ...filter });
  const activeSeries = usageSeries(series);

  const spend = usage.data;
  const currency = spend?.currency ?? "USD";
  const ranges = spend?.ranges ?? [7, 30, 90];
  const rangeCost = spend?.rangeTotals.costMicros ?? 0;
  const filtering = Boolean(filter.model || filter.keyId);

  if (usage.isLoading) return <LoadingBlock />;
  if (usage.isError) return <Alert>{t("usageLoadFailed")}</Alert>;

  const hasTraffic = (spend?.rangeTotals.requests ?? 0) > 0;

  return (
    <div className="space-y-6">
      <Segmented
        label={t("activity.range")}
        value={days}
        onChange={setDays}
        options={ranges.map((option) => ({ value: option, label: `${option}d` }))}
      />

      <StatStrip
        totals={spend?.rangeTotals ?? EMPTY_TOTALS}
        currency={currency}
        caption={`Last ${days} days`}
        stale={usage.isFetching}
      />

      {!hasTraffic ? (
        <Card>
          <EmptyState
            icon="models"
            title={t("activity.empty", { count: days })}
            description={t("activity.emptyDescription")}
          />
        </Card>
      ) : (
        <>
          <Panel
            title={t("activity.daily", {
              series: t(`series.${activeSeries.key}`).toLocaleLowerCase(),
            })}
            action={
              <Segmented
                label={t("activity.chartSeries")}
                value={series}
                onChange={setSeries}
                options={USAGE_SERIES.map((option) => ({
                  value: option.key,
                  label: t(`series.${option.key}`),
                }))}
              />
            }
          >
            <div className="p-5">
              <UsageChart
                points={(spend?.days ?? []).map((day) => ({
                  day: day.day,
                  value: activeSeries.value(day),
                }))}
                format={(value) => activeSeries.format(value, currency)}
                label={t("activity.daily", {
                  series: t(`series.${activeSeries.key}`).toLocaleLowerCase(),
                })}
                emptyLabel={t("activity.noRequests")}
              />
            </div>
          </Panel>

          {/*
            Side by side, because they are the same total split two ways and the
            interesting reading is across them: one model dominating while one key
            dominates is a different situation from one model spread over four keys.
          */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title={t("activity.byModel")}>
              <BreakdownList
                rows={(spend?.byModel ?? []).map((row) => ({
                  id: row.model,
                  label: row.model,
                  mono: true,
                  row,
                }))}
                total={rangeCost}
                currency={currency}
                activeId={filter.model ?? null}
                onSelect={(id) =>
                  setFilter((current) =>
                    current.model === id ? { ...current, model: undefined } : { ...current, model: id },
                  )
                }
              />
            </Panel>

            <Panel title={t("activity.byKey")}>
              <BreakdownList
                rows={(spend?.byKey ?? []).map((row) => ({
                  // A deleted key has no id to filter on, so its row is a readout
                  // rather than a control. Its spend still counts towards the
                  // total, which is why it is listed at all.
                  id: row.keyId,
                  label: keySpendLabel(row, t),
                  mono: false,
                  hint: row.prefix ?? undefined,
                  row,
                }))}
                total={rangeCost}
                currency={currency}
                activeId={filter.keyId ?? null}
                onSelect={(id) =>
                  setFilter((current) =>
                    current.keyId === id ? { ...current, keyId: undefined } : { ...current, keyId: id },
                  )
                }
              />
            </Panel>
          </div>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
              <h2 className="micro">
                {t("activity.recent")}
                <span className="ml-2 tracking-normal normal-case text-fg-subtle">
                  {t("activity.newestFirst", { count: spend?.recentLimit ?? 50 })}
                </span>
              </h2>
              {filtering && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-fg-muted">
                    {t("activity.filteredTo")}{" "}
                    <Mono className="text-xs text-fg">
                      {filter.model ??
                        keySpendLabel(
                          spend?.byKey.find((row) => row.keyId === filter.keyId) ?? {
                            name: null,
                            revoked: false,
                          },
                          t,
                        )}
                    </Mono>
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setFilter({})}>
                    {t("activity.clear")}
                                        </Button>
                </div>
              )}
            </div>

            {(spend?.recent.length ?? 0) === 0 ? (
              <p className="px-4 py-6 text-sm text-fg-subtle">
                No requests match this filter in the last {days} days.
              </p>
            ) : (
              <Table
                head={
                  <tr>
                    <Th priority="sm">{t("activity.when")}</Th>
                    <Th>{t("activity.model")}</Th>
                    <Th priority="lg">{t("activity.key")}</Th>
                    {/* In and out apart rather than one Tokens column. The two
                        are priced differently enough that their sum is the one
                        arrangement of these numbers that explains nothing. */}
                    <Th priority="md" className="text-right">
                      {t("activity.input")}
                    </Th>
                    <Th priority="md" className="text-right">
                      {t("activity.output")}
                    </Th>
                    <Th className="text-right">{t("activity.cost")}</Th>
                    {/*
                      Unlabelled, and narrow. This column carries a mark rather
                      than a value — a header over it would be a word wider than
                      everything beneath it, and "Cache" above a column blank on
                      most rows reads as one that failed to load.
                    */}
                    <Th className="w-8">
                      <span className="sr-only">{t("activity.cache")}</span>
                    </Th>
                  </tr>
                }
              >
                {(spend?.recent ?? []).map((request) => (
                  <tr key={request.id}>
                    <Td priority="sm" className="whitespace-nowrap text-fg-subtle">
                      {relativeTime(request.createdAt)}
                    </Td>
                    <Td>
                      <Mono className="text-xs">{request.model}</Mono>
                      {request.aborted && (
                        <span className="ml-2 text-[11px] text-fg-subtle">{t("activity.disconnected")}</span>
                      )}
                    </Td>
                    <Td priority="lg" className="text-fg-muted">
                      {request.keyName ?? (
                        <span className="text-fg-subtle">{t("activity.deletedKey")}</span>
                      )}
                    </Td>
                    <Td priority="md" className="text-right font-mono text-xs tabular-nums">
                      {formatExactTokens(request.inputTokens)}
                    </Td>
                    <Td priority="md" className="text-right font-mono text-xs tabular-nums">
                      {formatExactTokens(request.outputTokens)}
                    </Td>
                    {/* Zero is a real cost here, from either of two causes: a
                        free model, or a call below one micro that the meter
                        accumulates rather than charging for on its own. Muted
                        rather than hidden — the row still happened.

                        That set used to be much larger. At two decimal places
                        every request under half a cent landed here too, so a
                        27-micro call was indistinguishable from a free one in
                        the same column; six places leaves only the two causes
                        this comment names. */}
                    <Td
                      className={cx(
                        "text-right font-mono text-xs tabular-nums",
                        request.costMicros === 0 && "text-fg-subtle",
                      )}
                    >
                      {formatUsageCost(request.costMicros, currency)}
                    </Td>
                    {/*
                      Presence, not a number.

                      This was a column of counts, and on a caching client every
                      row carried one — a wall of figures beside the two that
                      actually move the bill. What a reader wants off a row is
                      whether the cache was involved at all. The count still
                      matters, so it rides on the mark rather than disappearing:
                      one hover away instead of permanently in the way.

                      Absent when nothing was cached, and equally absent when the
                      provider reported nothing. The numeric column drew that
                      distinction and it is real — it is simply not worth a glyph
                      on every row, and the strip above still separates them for
                      the window as a whole.
                    */}
                    <Td className="w-8 text-right">
                      {(request.cachedInputTokens ?? 0) > 0 && (
                        <span
                          title={t("activity.cacheTitle", {
                            cached: request.cachedInputTokens!.toLocaleString(),
                            input: request.inputTokens.toLocaleString(),
                          })}
                          className="inline-flex text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg-muted"
                        >
                          <Icon name="cache" className="size-3.5" />
                          <span className="sr-only">
                            {t("activity.cacheSr", { cached: request.cachedInputTokens!.toLocaleString() })}
                          </span>
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          {/* See OverviewTab for why a zero total says so in words. */}
          <p className="text-xs leading-6 text-fg-subtle">
            {rangeCost === 0 && (
              <>
                {t("activity.nothingCharged")}{" "}
              </>
            )}
            {t("activity.metered")}
            {/*
              Said plainly, and said only where the figures are actually on
              screen. A "cached" column beside a bill invites exactly one
              assumption — that a cache hit is cheaper — and it is not true here
              yet. Letting somebody discover that by reconciling their own
              numbers would be worse than a sentence.

              The counts are shown regardless of the price, because they explain
              the token totals above them: without this, a prompt sent a hundred
              times reads as a hundred full-price prompts with nothing saying
              ninety-nine of them were served from a cache.
            */}
            {(spend?.rangeTotals.cachedInputTokens ?? 0) > 0 &&
              ((spend?.rangeTotals.cachedSavingMicros ?? 0) > 0 ? (
                <> {t("activity.cachedBilled")}</>
              ) : (
                <> {t("activity.cachedPlain")}</>
              ))}
          </p>
        </>
      )}
    </div>
  );
}

interface BreakdownRow {
  id: string | null;
  label: string;
  mono: boolean;
  hint?: string;
  row: InferenceModelSpend | InferenceKeySpend;
}

/**
 * Cache reads as a whole-number share of the input they came out of, or null.
 *
 * Null rather than zero when nothing was cached, because a permanent "0% cached"
 * beside every model would say something false: most providers report no cache
 * detail at all, and their silence sums to zero identically to a genuine miss.
 * An absent line claims nothing; a zero claims the cache was cold.
 *
 * Rounded to whole percent — this is a health signal, and a second decimal place
 * on it invites the question of what the first one means.
 */
function cacheShare(row: { cachedInputTokens: number; inputTokens: number }): number | null {
  if (row.cachedInputTokens <= 0 || row.inputTokens <= 0) return null;
  return Math.round((row.cachedInputTokens / row.inputTokens) * 100);
}

/**
 * One breakdown, as rows that are also filter controls.
 *
 * Clicking a row filters the request log to it, and clicking it again clears —
 * which is why the pressed state has to be visible rather than implied by the
 * banner above the table. A row with no id (a deleted key) renders as plain text:
 * there is nothing to filter on, and a control that looks live and does nothing is
 * worse than no control.
 */
function BreakdownList({
  rows,
  total,
  currency,
  activeId,
  onSelect,
}: {
  rows: BreakdownRow[];
  total: number;
  currency: string;
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation("ai");
  if (rows.length === 0) {
    return <p className="px-4 py-6 text-sm text-fg-subtle">{t("activity.nothingHere")}</p>;
  }

  return (
    <ul className="divide-y divide-line">
      {rows.map((entry) => {
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-4">
              <span className="flex min-w-0 items-baseline gap-2">
                <span
                  className={cx(
                    "truncate",
                    entry.mono ? "font-mono text-xs text-fg" : "text-[13px] text-fg",
                  )}
                >
                  {entry.label}
                </span>
                {entry.hint && (
                  <span className="shrink-0 font-mono text-[11px] text-fg-subtle">
                    {entry.hint}
                  </span>
                )}
              </span>
              <span className="shrink-0 font-mono text-xs tabular-nums text-fg-muted">
                {formatUsageCost(entry.row.costMicros, currency)}
              </span>
            </div>
            <div className="mt-0.5 flex items-baseline justify-between gap-4">
              <span className="text-[11px] text-fg-subtle">
                {entry.row.requests.toLocaleString()}{" "}
                {entry.row.requests === 1 ? "request" : "requests"}
                {/*
                  The hit rate, where there is one to report. It belongs on this
                  row rather than in a column of its own because it is the single
                  number that explains why one model's cost per request is a
                  fraction of another's — and for a coding tool pointed at this
                  endpoint, it is the number that says the tool is configured
                  well.

                  A share of input tokens, since that is what the count is a
                  subset of. Against the combined total it would read low by
                  whatever the model happened to generate.
                */}
                {cacheShare(entry.row) !== null && (
                  <span className="ml-2 text-fg-muted">{cacheShare(entry.row)}% cached</span>
                )}
              </span>
              {/*
                In and out apart rather than summed, matching the request table.
                One model can cost four times another on the same token total by
                putting it at the other end.
              */}
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-fg-subtle">
                {formatRequestTokens(entry.row.inputTokens)} in ·{" "}
                {formatRequestTokens(entry.row.outputTokens)} out
              </span>
            </div>
            <ShareRule value={entry.row.costMicros} total={total} />
          </>
        );

        if (entry.id === null) {
          return (
            <li key={entry.label} className="px-4 py-3">
              {body}
            </li>
          );
        }

        const active = entry.id === activeId;
        return (
          <li key={entry.id}>
            <button
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(entry.id!)}
              className={cx(
                "w-full px-4 py-3 text-left transition-colors duration-200 ease-smooth",
                active ? "bg-accent-soft" : "hover:bg-raised",
              )}
            >
              {body}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
